/**
 * Settings Modal Component
 *
 * Modal for app settings including language preference and trading profile
 */

'use client';

import { useState, useEffect } from 'react';
import { Globe, Settings, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/language-context';
import { LANGUAGES, type LanguagePreference } from '@/lib/language';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BACKEND_URL_HEADER, getSelectedBackendUrl } from '@/lib/backend-config';
import { isDevelopmentDeploy } from '@/lib/deploy-env';
import { logger } from '@/lib/logger';
import { useAuth } from '@/contexts/auth-context';
import { getFeatureRestriction, normalizeUserAccess } from '@/lib/user-access';
import { FeatureAccessDialog } from '@/components/feature-access-dialog';

const ONBOARDING_STATUS_CACHE_TTL_MS = 5 * 60 * 1000;

interface OnboardingStatusCache {
  cachedAt: number;
  onboarding_completed: boolean;
  onboarding_dismissed: boolean;
  trading_years: number | null;
  trader_type: string | null;
  risk_tolerance: string | null;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getOnboardingCacheKey = (userId: string) => `onboarding-status:${userId}`;

const readCachedStatus = (userId: string): OnboardingStatusCache | null => {
  try {
    const raw = localStorage.getItem(getOnboardingCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingStatusCache;
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt > ONBOARDING_STATUS_CACHE_TTL_MS) {
      localStorage.removeItem(getOnboardingCacheKey(userId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedStatus = (userId: string, data: Omit<OnboardingStatusCache, 'cachedAt'>) => {
  try {
    localStorage.setItem(
      getOnboardingCacheKey(userId),
      JSON.stringify({
        ...data,
        cachedAt: Date.now(),
      } satisfies OnboardingStatusCache),
    );
  } catch {
    // Ignore localStorage failures silently.
  }
};

export default function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { preference, setPreference, t } = useLanguage();
  const { user, isLogin } = useAuth();
  const tradingProfileRestriction = getFeatureRestriction(normalizeUserAccess(user).features.trading_prefs, isLogin);

  // Trading profile state
  const [tradingYears, setTradingYears] = useState<string>('');
  const [traderType, setTraderType] = useState<string>('');
  const [riskTolerance, setRiskTolerance] = useState<string>('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [profileAccessDialogOpen, setProfileAccessDialogOpen] = useState(false);

  // Map trader type values to translation keys
  const TRADER_TYPE_KEYS: Record<string, string> = {
    day_trading: 'onboarding.dayTrading',
    swing_trading: 'onboarding.swingTrading',
    long_term_investing: 'onboarding.longTermInvesting',
  };

  // Map risk tolerance values to translation keys
  const RISK_KEYS: Record<string, string> = {
    conservative: 'onboarding.conservative',
    moderate: 'onboarding.moderate',
    aggressive: 'onboarding.aggressive',
  };

  const applyProfile = (data: Pick<OnboardingStatusCache, 'trading_years' | 'trader_type' | 'risk_tolerance'>) => {
    if (data.trading_years !== null && data.trading_years !== undefined) {
      setTradingYears(String(data.trading_years));
    } else {
      setTradingYears('');
    }
    setTraderType(data.trader_type || '');
    setRiskTolerance(data.risk_tolerance || '');
  };

  const showSaveMessage = (message: string) => {
    setSaveMessage(message);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const getSaveErrorMessage = async (response: Response) => {
    let errorMessage = '';

    try {
      const data = await response.json();
      if (typeof data?.error === 'string') errorMessage = data.error;
      if (!errorMessage && typeof data?.message === 'string') errorMessage = data.message;
    } catch {
      // Ignore non-JSON error responses and fall back to status-based messages.
    }

    switch (response.status) {
      case 400:
        return 'Missing user ID. Please refresh and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 422:
        return errorMessage || 'Please check your trading profile values and try again.';
      case 429:
        return 'Too many requests. Please try again tomorrow.';
      case 500:
        return 'We could not save your trading profile. Please try again.';
      default:
        return errorMessage || 'Unable to save your trading profile right now. Please try again.';
    }
  };

  // Load current trading profile when modal opens
  useEffect(() => {
    if (!isOpen) {
      setProfileLoaded(false);
      setSaveMessage('');
      return;
    }

    // Reset fields before fetching to avoid showing stale values
    setTradingYears('');
    setTraderType('');
    setRiskTolerance('');

    const loadProfile = async () => {
      try {
        if (!user?.userId) {
          setProfileLoaded(true);
          return;
        }

        const headers: Record<string, string> = {};
        const backendUrl = getSelectedBackendUrl();
        if (isDevelopmentDeploy() && backendUrl) {
          headers[BACKEND_URL_HEADER] = backendUrl;
        }

        const response = await fetch(`/api/onboarding/status?userId=${encodeURIComponent(String(user.userId))}`, { headers });
        if (response.ok) {
          const data = (await response.json()) as Omit<OnboardingStatusCache, 'cachedAt'>;
          const hasProfileData = data.trading_years !== null || data.trader_type !== null || data.risk_tolerance !== null || data.onboarding_completed;

          if (hasProfileData) {
            applyProfile(data);
            writeCachedStatus(String(user.userId), data);
          } else {
            const cached = readCachedStatus(String(user.userId));
            if (cached) {
              applyProfile(cached);
            }
          }
        }
      } catch (error) {
        logger.error('[Settings] Failed to load trading profile:', error);
        if (user?.userId) {
          const cached = readCachedStatus(String(user.userId));
          if (cached) {
            applyProfile(cached);
          }
        }
      } finally {
        setProfileLoaded(true);
      }
    };

    loadProfile();
  }, [isOpen, user?.userId]);

  if (!isOpen) return null;

  const handleLanguageChange = (newPreference: LanguagePreference) => {
    setPreference(newPreference);
  };

  const isProfileValid = tradingYears !== '' && Number(tradingYears) >= 0 && Number(tradingYears) <= 99 && traderType !== '' && riskTolerance !== '';

  const handleSaveProfile = async () => {
    if (!isProfileValid || isSavingProfile) return;

    setSaveMessage('');

    if (tradingProfileRestriction) {
      setProfileAccessDialogOpen(true);
      return;
    }

    setIsSavingProfile(true);

    try {
      if (!user?.userId) {
        showSaveMessage('Missing user ID. Please refresh and try again.');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const backendUrl = getSelectedBackendUrl();
      if (isDevelopmentDeploy() && backendUrl) {
        headers[BACKEND_URL_HEADER] = backendUrl;
      }

      const response = await fetch(`/api/onboarding?userId=${encodeURIComponent(String(user.userId))}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          trading_years: Number(tradingYears),
          trader_type: traderType,
          risk_tolerance: riskTolerance,
        }),
      });

      if (response.ok) {
        writeCachedStatus(String(user.userId), {
          onboarding_completed: true,
          onboarding_dismissed: false,
          trading_years: Number(tradingYears),
          trader_type: traderType,
          risk_tolerance: riskTolerance,
        });
        toast.success('Trading profile saved.', { position: 'top-center' });
        onClose();
      } else {
        showSaveMessage(await getSaveErrorMessage(response));
      }
    } catch (error) {
      logger.error('[Settings] Save profile error:', error);
      showSaveMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const selectItemClass = 'text-[#ccc] focus:bg-white/8 focus:text-white! focus:hover:text-white! hover:bg-white/8!';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Modal - Landscape orientation with max-height */}
      <div className="fixed left-1/2 top-1/2 z-100 flex max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-[#231F20] shadow-xl sm:max-h-[85vh]">
        {/* Header - Fixed */}
        <div className="flex flex-shrink-0 items-center border-b border-white/16 px-4 py-4 sm:px-6 sm:py-5">
          <Settings className="mr-3 h-5 w-5 shrink-0 text-white sm:h-6 sm:w-6" />
          <h2 className="min-w-0 flex-1 truncate text-xl font-bold text-white sm:text-2xl">{t('settings.title')}</h2>
          <X
            onClick={onClose}
            className="ml-3 h-6 w-6 shrink-0 cursor-pointer text-white"
          />
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-7 sm:space-y-8">
            {/* General Section */}
            <div>
              <h3 className="text-base font-semibold text-white mb-5 uppercase">{t('settings.general')}</h3>

              {/* Language Setting */}
              <div className="flex flex-col items-start gap-2 px-1 py-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="text-sm font-medium text-[#ccc]">{t('settings.language')}</label>
                <Select
                  value={preference}
                  onValueChange={(value) => handleLanguageChange(value as LanguagePreference)}>
                  <SelectTrigger className="w-full border-white/16 bg-[rgba(35,31,32,0.4)] sm:w-[200px]">
                    <SelectValue>
                      {preference === 'auto' ? <Globe className="text-sm text-white" /> : null}
                      <span className="text-sm text-white">
                        {preference === 'auto'
                          ? ` ${t('settings.autoDetect')}`
                          : `${LANGUAGES.find((l) => l.code === preference)?.flag} ${LANGUAGES.find((l) => l.code === preference)?.name}`}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent
                    align="end"
                    className="max-h-[300px] z-101 bg-[#3D3B3C] border border-white/10">
                    {/* Auto-detect option */}
                    <SelectItem
                      className={selectItemClass}
                      value="auto">
                      <span className="flex items-center gap-2">
                        <span>
                          <Globe className="text-sm text-white" />
                        </span>
                        <span>{t('settings.autoDetect')}</span>
                      </span>
                    </SelectItem>
                    {/* Language options */}
                    {LANGUAGES.map((lang) => (
                      <SelectItem
                        className={selectItemClass}
                        key={lang.code}
                        value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Trading Profile Section */}
            <div>
              <h3 className="text-base font-semibold text-white mb-5 uppercase">{t('settings.tradingProfile')}</h3>

              <div className="space-y-4 px-1">
                {/* Trading Experience */}
                <div className="flex flex-col items-start gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-medium text-[#ccc]">{t('settings.tradingExperience')}</label>
                  <div className="flex w-full items-center gap-2 sm:w-auto">
                    <Input
                      type="number"
                      min={0}
                      max={99}
                      placeholder={profileLoaded ? t('settings.notSet') : t('settings.loading')}
                      value={tradingYears}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || (Number(val) >= 0 && Number(val) <= 99)) {
                          setTradingYears(val);
                        }
                      }}
                      className="w-20 border border-white/16 text-right text-white"
                    />
                    <span className="text-sm text-white">{t('settings.years')}</span>
                  </div>
                </div>

                {/* Trading Style */}
                <div className="flex flex-col items-start gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-medium text-[#ccc]">{t('settings.tradingStyle')}</label>
                  <Select
                    value={traderType}
                    onValueChange={setTraderType}>
                    <SelectTrigger className="w-full border-white/16 bg-[rgba(35,31,32,0.4)] sm:w-[200px]">
                      <SelectValue placeholder={profileLoaded ? t('settings.notSet') : t('settings.loading')}>
                        {traderType ? <span className="text-sm text-white">{t(TRADER_TYPE_KEYS[traderType])}</span> : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                      className="z-101 bg-[#3D3B3C] border border-white/10"
                      align="end">
                      <SelectItem
                        className={selectItemClass}
                        value="day_trading">
                        {t('onboarding.dayTrading')}
                      </SelectItem>
                      <SelectItem
                        className={selectItemClass}
                        value="swing_trading">
                        {t('onboarding.swingTrading')}
                      </SelectItem>
                      <SelectItem
                        className={selectItemClass}
                        value="long_term_investing">
                        {t('onboarding.longTermInvesting')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Risk Appetite */}
                <div className="flex flex-col items-start gap-2 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-medium text-[#ccc]">{t('settings.riskAppetite')}</label>
                  <Select
                    value={riskTolerance}
                    onValueChange={setRiskTolerance}>
                    <SelectTrigger className="w-full border-white/16 bg-[rgba(35,31,32,0.4)] sm:w-[200px]">
                      <SelectValue
                        className="text-white"
                        placeholder={profileLoaded ? t('settings.notSet') : t('settings.loading')}>
                        {riskTolerance ? <span className="text-sm text-white">{t(RISK_KEYS[riskTolerance])}</span> : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                      className="z-101  bg-[#3D3B3C] border border-white/10"
                      align="end">
                      <SelectItem
                        className={selectItemClass}
                        value="conservative">
                        {t('onboarding.conservative')}
                      </SelectItem>
                      <SelectItem
                        className={selectItemClass}
                        value="moderate">
                        {t('onboarding.moderate')}
                      </SelectItem>
                      <SelectItem
                        className={selectItemClass}
                        value="aggressive">
                        {t('onboarding.aggressive')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Save button for trading profile */}
                <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
                  {saveMessage && <span className="text-sm text-red-500 sm:text-right">{saveMessage}</span>}
                  <Button
                    onClick={handleSaveProfile}
                    disabled={!isProfileValid || isSavingProfile}
                    className="w-full disabled:bg-[#666] disabled:text-[#ccc] not-disabled:bg-[#F98715] not-disabled:text-white not-disabled:hover:bg-[#F98715] sm:w-auto">
                    {isSavingProfile ? t('onboarding.saving') : t('settings.save')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FeatureAccessDialog
        open={profileAccessDialogOpen}
        onOpenChange={setProfileAccessDialogOpen}
        restriction={tradingProfileRestriction ?? 'upgrade'}
        feature="trading-profile"
      />
    </>
  );
}
